// Local archive of "detected" videos (original footage re-rendered with the
// scripted bounding boxes burned in - see Dashboard.jsx's downloadDetectedVideo).
// These are real video blobs, often tens of MB, so localStorage's ~5MB quota
// won't hold them - IndexedDB is the only browser-native store with enough
// headroom, and it's exactly what History's "Historical Saved Videos" panel
// reads from.

const DB_NAME = 'fsqa_video_archive';
const DB_VERSION = 1;
const STORE = 'videos';

function openDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Mirrors localHistory.js's DETECTIONS_KEY reset: the archive is meant to
// reflect what's been detected THIS session, not accumulate forever across
// unrelated days/testing sessions. This module (like localHistory.js) is
// only re-evaluated on a real browser refresh - client-side navigation
// between Dashboard/History/etc. does not reload it - so the archive still
// behaves normally while actually using the app, and only starts fresh on
// a hard reload.
(async () => {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // ignore - worst case the archive doesn't reset this one time
  }
})();

// record: { filename, camera, date, productId, className, mimeType, blob,
// thumbnail } - thumbnail is a small JPEG data URL (a frame with the box
// already burned in) so the saved-videos grid can show a real preview tile
// instead of just a filename.
export async function saveArchivedVideo(record) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const request = store.add({ ...record, created_at: Date.now() });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.onerror = () => reject(tx.error);
  });
}

export async function getAllArchivedVideos() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const request = store.getAll();
    request.onsuccess = () => resolve((request.result || []).sort((a, b) => b.created_at - a.created_at));
    request.onerror = () => reject(request.error);
  });
}

// Filenames are deterministic (CAM_Date_ProductID), so re-detecting the
// same clip on the same day would otherwise pile up identical-looking
// entries every time it's auto-archived or re-downloaded.
export async function findArchivedVideoByFilename(filename) {
  const all = await getAllArchivedVideos();
  return all.find((v) => v.filename === filename) || null;
}

// Used by the Dashboard's "Clear session log" button, so the one explicit
// clear action resets both stores together (mirrors the module-load IIFE
// above, just triggered on demand instead of on refresh).
export async function clearAllArchivedVideos() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteArchivedVideo(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
